import React, { useState } from 'react';
import { Bell, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { PhoneVerificationFlow } from '../Phone/PhoneVerificationFlow';
import { albumsApi } from '../../api/albums';
import { useAuthStore } from '../../store/authStore';
import styles from './NotificationOptInModal.module.css';

interface Props {
  albumId: string;
  albumName: string;
  faceRecognitionEnabled: boolean;
  isOpen: boolean;
  onDone: () => void;
  onEnabled?: () => void;
  /** 'empty-album' shows messaging tailored to an album that has no photos yet. */
  context?: 'empty-album';
}

type Step = 'ask' | 'add-phone' | 'confirm-phone' | 'done';

export const NotificationOptInModal: React.FC<Props> = ({
  albumId,
  albumName,
  faceRecognitionEnabled,
  isOpen,
  onDone,
  onEnabled,
  context,
}) => {
  const { user, fetchMe } = useAuthStore();
  const [step, setStep] = useState<Step>('ask');
  const [saving, setSaving] = useState(false);

  const hasFacePhoto = user?.hasFacePhoto ?? false;
  const existingPhone = user?.phone ?? null;

  async function enableNotifications() {
    setSaving(true);
    try {
      await albumsApi.updateNotifications(albumId, true);
      await fetchMe();
      setStep('done');
      onEnabled?.();
    } catch {
      toast.error('Failed to enable notifications. You can turn them on later from the album menu.');
      onDone();
    } finally {
      setSaving(false);
    }
  }

  function handleYes() {
    if (existingPhone) {
      setStep('confirm-phone');
    } else {
      setStep('add-phone');
    }
  }

  function handlePhoneVerified() {
    // fetchMe to pick up the new phone on the user object, then enable
    fetchMe().then(() => enableNotifications());
  }

  const title = step === 'add-phone' ? 'Add your phone number' : 'Get notified';

  return (
    <Modal isOpen={isOpen} onClose={onDone} title={title} size="sm">
      <div className={styles.body}>

        {step === 'ask' && (
          <>
            <div className={styles.iconWrap}>
              <Bell className={styles.bellIcon} />
            </div>

            <div className={styles.content}>
              {context === 'empty-album' ? (
                <>
                  <p className={styles.heading}>Be the first to know when photos land!</p>
                  <p className={styles.description}>
                    This album is fresh and waiting for photos. Drop your number and we'll send you a WhatsApp or SMS the moment photos are added to <strong>"{albumName}"</strong>.
                  </p>
                </>
              ) : (
                <>
                  <p className={styles.heading}>Stay in the loop for <strong>{albumName}</strong></p>
                  <p className={styles.description}>
                    Get a WhatsApp or SMS message when new photos are added to this album
                    {faceRecognitionEnabled && hasFacePhoto
                      ? ' — including photos of you found by face recognition.'
                      : '.'}
                  </p>
                </>
              )}
            </div>

            <div className={styles.actions}>
              <div className={styles.btnWrap}>
                <Button variant="primary" fullWidth onClick={handleYes}>
                  <Bell size={14} />
                  Yes, notify me
                </Button>
              </div>
              <Button variant="ghost" onClick={onDone}>
                Skip for now
              </Button>
            </div>
          </>
        )}

        {step === 'add-phone' && (
          <>
            <p className={styles.subtext}>
              We'll send updates via WhatsApp or SMS. Enter your number to verify.
            </p>
            <PhoneVerificationFlow onVerified={handlePhoneVerified} />
            <button className={styles.skipLink} onClick={onDone}>Skip for now</button>
          </>
        )}

        {step === 'confirm-phone' && (
          <>
            <div className={styles.content}>
              <p className={styles.description}>
                We'll send updates to <strong>{existingPhone}</strong> via WhatsApp or SMS.
              </p>
            </div>
            <div className={styles.actions}>
              <div className={styles.btnWrap}>
                <Button variant="primary" fullWidth onClick={enableNotifications} loading={saving}>
                  Confirm
                </Button>
              </div>
              <Button variant="ghost" onClick={onDone} disabled={saving}>
                Skip for now
              </Button>
            </div>
          </>
        )}

        {step === 'done' && (
          <>
            <div className={styles.successWrap}>
              <CheckCircle2 className={styles.successIcon} />
              <p className={styles.successText}>Notifications enabled</p>
              <p className={styles.successSub}>
                We'll message you when new photos land in <strong>{albumName}</strong>.
              </p>
            </div>
            <div className={styles.actions}>
              <div className={styles.btnWrap}>
                <Button variant="primary" fullWidth onClick={onDone}>
                  View album
                </Button>
              </div>
            </div>
          </>
        )}

      </div>
    </Modal>
  );
};
